import { tracked } from '@glimmer/tracking';
import cloneDeep from 'lodash.clonedeep';

import Constants from '../utils/constants';
import Utils from '../utils/utils';

import DefaultAmp from './signal-chain/default-amp';
import VoltageReferenceAmp from './signal-chain/voltage-reference-amp';
import Dac from './signal-chain/dac';

const SIGNAL_CHAIN_ITEMS = Constants.SIGNAL_CHAIN_ITEMS;
const CDP_PARAMETERS = Constants.CDP.PARAMETERS;

export default class SignalChainHandler {
  // Set upon construction and remaining
  cdpParameters = null;
  dac = null;
  customObjects = {};

  @tracked dacTypeOptions = [];
  @tracked availableItems = [];
  @tracked data = {
    internalVref: null,
    outputRange: null,
  };
  @tracked gridData = null;
  @tracked errorData = {};

  constructor(cdpParameters) {
    this.cdpParameters = cdpParameters;
    this.dac = new Dac(this.cdpParameters);
    // Any custom signal chain handlers used in this file need to be configured here
    this.customObjects[SIGNAL_CHAIN_ITEMS.VOLTAGE_REFERENCE] = VoltageReferenceAmp;
    this.customObjects[SIGNAL_CHAIN_ITEMS.INTERNAL_VOLTAGE_REFERENCE] = VoltageReferenceAmp;
  }

  get currentItems() {
    return this.availableItems.filter((item) => item.isCurrent);
  }

  get voltageReference() {
    return this.currentItems.find((item) => item.isVoltageReference);
  }

  get voltageReferenceValue() {
    return this.voltageReference && !this.voltageReference.isInternal && this.voltageReference.selectedVout
      ? this.voltageReference.selectedVout.value
      : null;
  }

  get inputVoltage() {
    return Utils.isNumeric(this.voltageReferenceValue) ? this.voltageReferenceValue : this.data.internalVref;
  }

  get internalVrefOptions() {
    let selectedVrefLevelInternal = this.dac.selectedVrefLevelInternal;
    if (!selectedVrefLevelInternal.displayValue) return [];

    return selectedVrefLevelInternal.displayValue
      .split(',')
      .map((item) => {
        let itemValue = null;
        const itemValueParts = item.trim().split(' ');
        if (itemValueParts.length > 0 && Utils.isNumeric(itemValueParts[0])) itemValue = Number(itemValueParts[0]);

        return {
          displayValue: item.trim(),
          value: itemValue,
        };
      })
      .filter((item) => item.value);
  }

  get topologyInputData() {
    return {
      internalVrefOptions: this.internalVrefOptions,
      outputRangeOptions: this.dac.selectedOutputRangeOptions,
    };
  }

  load(dacTypeOptions) {
    const options = (dacTypeOptions || []).map((item) => {
      let dacOption = cloneDeep(item);
      Utils.generateNamingMetadata(dacOption);
      return dacOption;
    });

    this.dacTypeOptions = options;
  }

  handleResistorParamsChange(resistorId, paramName, newValue) {
    this.availableItems = this.availableItems.map((item) => {
      if (item.resistor && item.resistor.id === resistorId) {
        item.resistor[paramName] = newValue;
        return item;
      }
      return item;
    });
  }

  handleSignalChainChange(dacSignalChainItems, gridData, availableResistors) {
    this.gridData = gridData || [];
    this.errorData = {};
    this.updateAvailableItems(dacSignalChainItems || [], availableResistors);
  }

  handleDacTypeChange(type, gridData) {
    this.errorData = {};
    this.dac.handleDacTypeChange(type, gridData);
  }

  handleSelectedDacChange(dacType) {
    this.errorData = {};
    this.dac.handleSelectedDacChange(dacType);
    /**
     * DEBT-194 & DEBT-195
     * With this change we are resetting every selected signal chain item whenever the selected DAC is updated
     *
     * This is also necessary for when we are using the selected dac in place of the signal chain item
     **/
    this.availableItems.forEach((item) => {
      item.selected = this.findDefaultSelectedSignalChainItem(item.config);
    });
  }

  useSelectedDacForSignalChainItem() {
    /**
     * If this is ever done for signal chain items other than voltage reference,
     * we may need a way to determine what kind of item it is
     */
    let result = this.dac.selected ? cloneDeep(this.dac.selected) : {};
    result[CDP_PARAMETERS.INITIAL_ACCURACY] = result[CDP_PARAMETERS.VREF_INITIAL_ACCURACY_MAX];
    result[CDP_PARAMETERS.VOUT_TEMPCO_MAX] = result[CDP_PARAMETERS.VREF_TEMPCO_MAX];
    result[CDP_PARAMETERS.V_NOISE] = result[CDP_PARAMETERS.VREF_V_NOISE];

    return result;
  }

  handleTopologyInputChange(topologyInputs) {
    this.resetCurrentItems(topologyInputs);
    this.resetData(topologyInputs);
  }

  isItemInCurrentTopology(item, topologyInputs) {
    const foundInput = topologyInputs.find((input) => {
      const selectedItems =
        input.selectedOption && input.selectedOption.signalChainItems ? input.selectedOption.signalChainItems : [];
      const found = selectedItems.find((selected) => selected === item.config.id);
      return !!found;
    });

    return !!foundInput;
  }

  resetData(topologyInputs) {
    // reset signal chain handler data
    const newData = {};
    Object.keys(this.data).forEach((key) => {
      newData[key] = null;
    });

    (topologyInputs || [])
      // find inputs with associated data for the signal chain
      .filter(
        (input) =>
          input.selectedOption.associatedData &&
          input.selectedOption.associatedData.isInSignalChain &&
          input.selectedOption.associatedData.dataLocation &&
          input.selectedOption.associatedData.item,
      )
      .forEach((input) => {
        let associatedData = input.selectedOption.associatedData;
        newData[associatedData.dataLocation] = associatedData.setEntireItem
          ? associatedData.item
          : associatedData.item.value;
      });
    this.data = newData;
  }

  resetCurrentItems(topologyInputs) {
    // merge signal chain items from each input's selected option
    this.availableItems.forEach((item) => {
      item.isCurrent = this.isItemInCurrentTopology(item, topologyInputs);
    });
  }

  updateAvailableItems(items, availableResistors) {
    const availableItems = items.map((item) => this.generateAvailableItem(item, availableResistors));
    this.availableItems = availableItems;
  }

  generateAvailableItem(item, availableResistors) {
    const config = cloneDeep(item),
      resistor =
        availableResistors && item.resistor
          ? availableResistors.find((resistor) => resistor.id === item.resistor)
          : null;

    Utils.generateNamingMetadata(config);

    let result = {
      isCurrent: false,
      config: config,
      selected: this.findDefaultSelectedSignalChainItem(config),
      cdpParameters: this.cdpParameters,
    };

    if (resistor) result.resistor = resistor;

    if (config.id in this.customObjects) {
      return new VoltageReferenceAmp(result);
    } else {
      return new DefaultAmp(result);
    }
  }

  findDefaultSelectedSignalChainItem(config) {
    let selected = null;

    if (!config.noPartSelection && config.pst.id in this.gridData) {
      const gridData = this.gridData[config.pst.id];
      if (gridData && gridData.view && gridData.data) {
        const defaultItem = config.defaultFn ? config.defaultFn({ selectedDac: this.dac.selected }) : config.default;
        selected = gridData.data.data.find((item) => item.name === defaultItem);
        if (!selected)
          console.warn(
            `The default part (${defaultItem}) could not be found in signal chain item (${config.name}) pst (${config.pst.id}) grid data`,
          );
      } else console.warn(`Grid data could not be found for signal chain item (${config.name}) pst (${config.pst.id})`);
    } else if (!config.noPartSelection)
      console.warn(`Grid data could not be found for signal chain item (${config.name}) pst (${config.pst.id})`);
    else if (config.useSelectedDac) selected = this.useSelectedDacForSignalChainItem();

    return selected;
  }

  getErrorValue(errorKey) {
    return errorKey in this.errorData ? this.errorData[errorKey] : null;
  }

  updateErrorValue(errorKey, errorValue) {
    this.errorData[errorKey] = errorValue;
    this.errorData['lastUpdateDate'] = new Date();
    //eslint-disable-next-line no-self-assign
    this.errorData = this.errorData;
  }
}
